import { ReactComponent as CloseIcon } from '../../../../../../../assets/icons/ic_close.svg';
import { ReactComponent as CashIcon } from '../../../../../../../assets/icons/Money.svg';
import { ReactComponent as CardIcon } from '../../../../../../../assets/icons/CreditCard.svg';
import { ReactComponent as BankIcon } from '../../../../../../../assets/icons/Bank.svg';
import { ReactComponent as OtherIcon } from '../../../../../../../assets/icons/DotsThree.svg';
import { AmountFormatHelper } from '../../../../../../../utilities/helpers/AmountFormatHelper';
import { useDictionary } from '../../../../../../../utilities/hooks/UseDictionary';
import { UseCurrencyDecorator } from '../../../../../../../utilities/hooks/UseCurrencyDecorator';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

const METHODS = ['cash', 'card', 'bank_transfer', 'other'];

/**
 * Form shown inside the Add payment / Add refund popover.
 * `mode` switches all copy, the helper amount and the submit action between
 * the payment and refund variants — the layout is identical for both.
 */
const RecordTransactionPopover = ({ mode, details, onClose, onSubmit }) => {
	const dictionary = useDictionary();
	const currencyDecorator = UseCurrencyDecorator();

	const isRefund = mode === 'refund';

	// TODO(api): confirm the real source of these amounts once the
	// record-payment / record-refund endpoints exist.
	const totalPaid = useMemo(
		() =>
			(details?.payments || []).reduce(
				(sum, payment) => sum + (payment.amount || 0),
				0
			),
		[details]
	);
	// Booking amounts are stored as integers (x100); convert to the decimal
	// value the input works with — same as the change-amount dialog.
	const bookingTotal = details?.invoice?.total ?? details?.amount ?? 0;
	const limitRaw = isRefund ? totalPaid : Math.max(bookingTotal - totalPaid, 0);
	const limit = AmountFormatHelper.formatAmount(limitRaw);

	// Both modes default the amount to the full limit (outstanding for a
	// payment, total paid for a refund) and pre-fill the reference with the
	// matching "Payment/Refund for booking #<id>" pattern.
	const [amount, setAmount] = useState(limit ? String(limit) : '');
	const [method, setMethod] = useState('cash');
	const [reference, setReference] = useState(
		`${
			isRefund
				? dictionary.refund_reference_prefix
				: dictionary.payment_reference_prefix
		}${details?.display_id ?? ''}`
	);
	const [submitting, setSubmitting] = useState(false);

	// The booking now carries a currency object ({ code, symbol, decimals });
	// use it for both the input adornment and the formatted helper amounts.
	const currency = details?.currency;
	const currencyCode = currency?.code ?? 'EUR';

	const methodLabels = {
		cash: dictionary.payment_method_cash,
		card: dictionary.payment_method_card,
		bank_transfer: dictionary.payment_method_bank_transfer,
		other: dictionary.payment_method_other
	};

	const methodIcons = {
		cash: CashIcon,
		card: CardIcon,
		bank_transfer: BankIcon,
		other: OtherIcon
	};

	// The number input always yields a dot-decimal string regardless of locale.
	const parsedAmount = parseFloat(amount) || 0;
	// The API requires a positive amount and a non-empty reference.
	const isValid = parsedAmount > 0 && reference.trim() !== '';

	const toCurrency = (value) => currencyDecorator(value, currency);

	const handleSubmit = async () => {
		if (!isValid || submitting) {
			return;
		}
		setSubmitting(true);
		try {
			await onSubmit({
				// The API stores amounts as integers (x100).
				amount: AmountFormatHelper.deFormatAmount(parsedAmount),
				// The API reads the payment method under the `type` key.
				type: method,
				reference: reference.trim()
			});
			// On success the parent closes the popover (this unmounts).
		} catch (error) {
			// Keep the popover open so the user can retry; the parent toasts.
			setSubmitting(false);
		}
	};

	return (
		<div className="record-transaction" data-testid={`record-${mode}-form`}>
			<div className="record-transaction-header">
				<h4 className="record-transaction-title">
					{isRefund ? dictionary.record_refund : dictionary.record_payment}
				</h4>
				<button
					type="button"
					className="record-transaction-close"
					onClick={onClose}
					aria-label={dictionary.close}
					data-testid="record-transaction-close"
				>
					<CloseIcon />
				</button>
			</div>

			<div className="record-transaction-section">
				<div className="record-transaction-label">{dictionary.amount}</div>
				<div className="record-amount-field">
					<span className="record-amount-currency">{currencyCode}</span>
					<input
						className="record-amount-input"
						type="number"
						min="0"
						step="any"
						value={amount}
						onChange={(event) => setAmount(event.target.value)}
						data-testid="record-amount-input"
					/>
				</div>
				<div className="record-transaction-helper">
					{isRefund ? dictionary.refundable_up_to : dictionary.remaining}
					&nbsp;{toCurrency(limit)}
				</div>
			</div>

			<div className="record-transaction-section">
				<div className="record-transaction-label">
					{isRefund ? dictionary.refund_method : dictionary.payment_method}
				</div>
				<div className="record-transaction-methods">
					{METHODS.map((item) => {
							const MethodIcon = methodIcons[item];
							return (
								<button
									key={item}
									type="button"
									className={
										item === method
											? 'record-method-button record-method-button-active'
											: 'record-method-button'
									}
									onClick={() => setMethod(item)}
									data-testid={`record-method-${item}`}
								>
									<MethodIcon className="record-method-icon" />
									{methodLabels[item]}
								</button>
							);
						})}
				</div>
			</div>

			<div className="record-transaction-section">
				<div className="record-transaction-label">{dictionary.reference}</div>
				<input
					className="record-reference-input"
					type="text"
					value={reference}
					onChange={(event) => setReference(event.target.value)}
					placeholder={
						isRefund
							? dictionary.refund_reference_placeholder
							: dictionary.payment_reference_placeholder
					}
					data-testid="record-reference-input"
				/>
			</div>

			<div className="record-transaction-footer">
				<button
					type="button"
					className="button record-transaction-submit"
					disabled={!isValid || submitting}
					onClick={handleSubmit}
					data-testid={`record-${mode}-submit`}
				>
					{isRefund
						? dictionary.record_refund_action
						: dictionary.record_payment_action}
					&nbsp;&nbsp;{toCurrency(parsedAmount)}
				</button>
			</div>
		</div>
	);
};

RecordTransactionPopover.propTypes = {
	mode: PropTypes.oneOf(['payment', 'refund']).isRequired,
	details: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired
};

RecordTransactionPopover.defaultProps = {
	details: null
};

export default RecordTransactionPopover;
